
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Ghost, 
  Search, 
  Zap, 
  ShieldAlert, 
  AlertCircle, 
  Lock, 
  Globe, 
  Terminal, 
  Eye, 
  Database,
  ShieldCheck,
  RefreshCw,
  Mail,
  User,
  ShieldX,
  History,
  Download
} from 'lucide-react';

interface Breach {
  id: string;
  source: string;
  type: 'Credential Leak' | 'Database Dump' | 'Chat Mention';
  severity: 'Critical' | 'High' | 'Medium';
  date: string;
  status: 'Open' | 'Mitigated' | 'Monitoring';
  affected: string;
}

const MOCK_BREACHES: Breach[] = [
  { id: 'DW-901', source: 'BreachForums', type: 'Credential Leak', severity: 'Critical', date: '2h ago', status: 'Open', affected: 'admin@minerva.com' },
  { id: 'DW-902', source: 'RaidForums (Archive)', type: 'Database Dump', severity: 'High', date: '1d ago', status: 'Mitigated', affected: 'Corporate DB Segment A' },
  { id: 'DW-903', source: 'Telegram / CyberCops', type: 'Chat Mention', severity: 'Medium', date: '3d ago', status: 'Monitoring', affected: 'Brand Reputation' },
];

export const DarkwebView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedEmail, setScannedEmail] = useState('');

  const handleScan = () => {
    if (!scannedEmail) return;
    setIsScanning(true);
    setTimeout(() => setIsScanning(false), 3000);
  };

  return (
    <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-3 bg-white border border-slate-200 rounded-2xl hover:bg-slate-100 transition-all text-slate-600 shadow-sm">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Darkweb Surveillance</h1>
            <p className="text-sm text-slate-500 font-medium tracking-tight">Monitoring TOR/I2P onion nodes for data leaks & corporate mentions.</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-slate-900 text-white px-6 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 shadow-lg transition-all active:scale-95 flex items-center space-x-2">
            <RefreshCw size={14} className={isScanning ? 'animate-spin' : ''} />
            <span>Force Global Sweep</span>
          </button>
        </div>
      </div>

      <div className="bg-slate-900 p-10 md:p-14 rounded-[3.5rem] shadow-2xl relative overflow-hidden group">
         <div className="relative z-10 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-[1.5rem] flex items-center justify-center text-white mb-6 shadow-xl shadow-blue-500/20">
               <Ghost size={32} />
            </div>
            <h2 className="text-white text-3xl font-black mb-2 tracking-tighter">Credential Leak Scanner</h2>
            <p className="text-slate-400 font-medium mb-10 max-w-md">Search across 14 billion leaked records from public and private breaches.</p>
            
            <div className="flex flex-col md:flex-row items-center bg-white/5 border border-white/10 rounded-3xl p-2 w-full max-w-2xl focus-within:ring-4 focus-within:ring-blue-500/20 transition-all gap-2">
               <input 
                 type="text" 
                 value={scannedEmail}
                 onChange={(e) => setScannedEmail(e.target.value)}
                 placeholder="Search by Email or Domain (e.g. corp-access@domain.com)" 
                 className="flex-1 bg-transparent border-none outline-none px-6 py-4 text-white font-medium text-sm placeholder:text-slate-500 w-full"
               />
               <button 
                 onClick={handleScan}
                 className="bg-blue-600 text-white px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all active:scale-95 w-full md:w-auto flex items-center justify-center"
               >
                 {isScanning ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Zap size={16} className="mr-2" />}
                 {isScanning ? 'Scanning Onion Nodes...' : 'Deep Scan'}
               </button>
            </div>
         </div>
          <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
            <div className="grid grid-cols-24 gap-1 w-full h-full font-mono text-[8px] text-blue-400">
               {Array.from({length: 200}).map((_, i) => (
                 <div key={i}>{((i * 0x12345678) % 0x10000).toString(16).padStart(4, '0')}</div>
               ))}
            </div>
         </div>
         <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-[140px] opacity-10 translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
               <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center">
                  <Database size={16} className="mr-2 text-blue-600" /> Active Leak Registry
               </h3>
               <span className="text-[10px] font-black bg-blue-50 text-blue-600 px-3 py-1 rounded-full border border-blue-100 uppercase">3 New Matches</span>
            </div>
            
            <div className="divide-y divide-slate-100">
               {MOCK_BREACHES.map((breach) => (
                 <div key={breach.id} className="p-8 hover:bg-slate-50 transition-all flex items-center justify-between group">
                    <div className="flex items-start space-x-6">
                       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm ${
                         breach.severity === 'Critical' ? 'bg-rose-50 text-rose-500' : 'bg-amber-50 text-amber-500'
                       }`}>
                          {breach.severity === 'Critical' ? <ShieldX size={28} /> : <AlertCircle size={28} />}
                       </div>
                       <div>
                          <div className="flex items-center space-x-4 mb-1">
                             <span className="text-base font-black text-slate-900 group-hover:text-blue-600 transition-colors uppercase">{breach.source}</span>
                             <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border ${
                               breach.severity === 'Critical' ? 'bg-rose-50 text-rose-700 border-rose-100' : 'bg-amber-50 text-amber-700 border-amber-100'
                             }`}>
                                {breach.severity} Severity
                             </span>
                          </div>
                          <p className="text-sm font-bold text-slate-700 mb-3">{breach.type}</p>
                          <div className="flex items-center space-x-4 text-[10px] font-black uppercase tracking-widest text-slate-400">
                             <span className="flex items-center"><User size={14} className="mr-2" /> {breach.affected}</span>
                             <span className="flex items-center"><History size={14} className="mr-2" /> {breach.date}</span>
                             <span className="flex items-center space-x-2">
                                <div className={`w-2 h-2 rounded-full ${breach.status === 'Open' ? 'bg-rose-500 animate-pulse' : 'bg-emerald-500'}`}></div>
                                <span>{breach.status}</span>
                             </span>
                          </div>
                       </div>
                    </div>
                    <button className="p-3 bg-white border border-slate-200 rounded-xl hover:bg-slate-900 hover:text-white transition-all shadow-sm">
                       <Eye size={20} />
                    </button>
                 </div>
               ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
             <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-6">Attack Surface Mapping</h3>
             <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                   <div className="flex items-center space-x-3">
                      <Terminal size={18} className="text-slate-400" />
                      <span className="text-xs font-black text-slate-700 uppercase">Leaked Logins</span>
                   </div>
                   <span className="text-sm font-black text-rose-600">142</span>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                   <div className="flex items-center space-x-3">
                      <Globe size={18} className="text-slate-400" />
                      <span className="text-xs font-black text-slate-700 uppercase">Exposed Ports</span>
                   </div>
                   <span className="text-sm font-black text-amber-600">4</span>
                </div>
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                   <div className="flex items-center space-x-3">
                      <ShieldCheck size={18} className="text-slate-400" />
                      <span className="text-xs font-black text-slate-700 uppercase">Brand Mentions</span>
                   </div>
                   <span className="text-sm font-black text-blue-600">2.4k</span>
                </div>
             </div>
             <button className="w-full mt-6 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95 flex items-center justify-center space-x-2">
                <Download size={14} />
                <span>Download Forensic Bundle</span>
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};
